import React, { useState, useEffect } from 'react';
import { libellePrixTotal } from './utils/honorairesModel';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PublicPropertyView = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Helper function to check if a value is valid for display
  const isValidValue = (value) => {
    return value !== null && value !== undefined && value !== 0 && value !== '' && value !== 'Aucune';
  };

  useEffect(() => {
    loadProperty();
  }, [code]);

  const loadProperty = async (pwd = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const url = `${API}/api/public/properties/${code}${pwd ? `?password=${pwd}` : ''}`;
      const response = await axios.get(url);
      
      setProperty(response.data);
      setPasswordRequired(false);
    } catch (err) {
      if (err.response?.status === 401) {
        setPasswordRequired(true);
      } else if (err.response?.status === 403) {
        setError('Ce bien n\'est pas accessible publiquement.');
      } else if (err.response?.status === 404) {
        setError('Ce bien n\'existe pas ou n\'est plus disponible.');
      } else {
        setError('Erreur lors du chargement du bien.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    loadProperty(password);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4 text-slate-400"><i className="bi bi-x-octagon"></i></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Bien non disponible</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (passwordRequired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4 text-slate-500"><i className="bi bi-lock-fill"></i></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Bien protégé</h1>
              <p className="text-gray-600">Ce bien nécessite un mot de passe.</p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Mot de passe"
                required
              />
              <button
                type="submit"
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Accéder au bien
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const photos = property.photos || [];
  const currentPhoto = photos[currentPhotoIndex];

  // iter131 — la projection publique est UNIFIÉE avec /f (objets imbriqués) :
  // cascade unique prix_cession (FAI) → net_vendeur (pivot) ; `prix_annonce` n'existe plus.
  const vente = property.informations_vente || {};
  const bail = property.informations_bail || {};
  const prixPublic = vente.prix_cession ?? vente.net_vendeur ?? property.prix_estime ?? null;
  const loyerMensuel = bail.loyer_mensuel ?? bail.redevance_mensuelle ?? null;
  const chargesMensuelles = bail.charges_mensuelles ?? bail.charges ?? null;


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <i className="bi bi-building text-white text-xl"></i>
              </div>
              <span className="font-bold text-xl text-gray-900">Immobilier Commercial</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Gallery */}
        {photos.length > 0 && (
          <div className="mb-8">
            <div className="relative rounded-2xl overflow-hidden bg-gray-200 aspect-[16/9] shadow-xl">
              <img
                src={currentPhoto?.url}
                alt="Photo du bien"
                className="w-full h-full object-cover"
              />
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex - 1 + photos.length) % photos.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <i className="bi bi-chevron-left text-xl"></i>
                  </button>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex + 1) % photos.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
                  >
                    <i className="bi bi-chevron-right text-xl"></i>
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {photos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhotoIndex(idx)}
                        className={`h-2 rounded-full transition-all ${
                          idx === currentPhotoIndex ? 'bg-white w-8' : 'bg-white/50 w-2'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content - GRID AVEC SIDEBAR */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main info - 2/3 largeur */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="border-b border-gray-200 pb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {property.transaction_type || 'Vente'}
                </span>
                {property.type_bien && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                    {property.type_bien}
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {property.titre_annonce || property.activite_principale || property.subcategory || 'Bien immobilier commercial'}
              </h1>
              
              {property.activite_principale && (
                <p className="text-lg text-gray-700 mb-3">
                  {property.activite_principale}
                </p>
              )}
              
              <div className="space-y-1 text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="bi bi-geo-alt"></i>
                  <span>
                    {property.secteur && `${property.secteur}, `}
                    {property.ville}
                    {property.code_postal && ` (${property.code_postal})`}
                  </span>
                </div>
                {property.zone_attractivite && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="bi bi-pin-map"></i>
                    <span className="font-medium">{property.zone_attractivite}</span>
                  </div>
                )}
                {property.emplacement && (
                  <div className="flex items-center gap-2 text-sm">
                    <i className="bi bi-shop"></i>
                    <span>Emplacement: {property.emplacement}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Prix et infos financières détaillées */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {prixPublic != null && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl col-span-2" data-testid="public-legacy-prix">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="bi bi-currency-euro text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 tabular-nums">
                      {prixPublic.toLocaleString('fr-FR')} €
                    </div>
                    <div className="text-sm text-gray-600">
                      {vente.prix_cession != null ? libellePrixTotal(vente.honoraires_charge) : 'Prix du bien'}
                    </div>
                  </div>
                </div>
              )}

              {property.superficie_totale && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="bi bi-rulers text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{property.superficie_totale}</div>
                    <div className="text-sm text-gray-600">m²</div>
                  </div>
                </div>
              )}
              
              {loyerMensuel != null && (
                <div className="p-3 bg-gray-50 rounded-lg" data-testid="public-legacy-loyer">
                  <div className="text-xs text-gray-600 mb-1">Loyer mensuel HT</div>
                  <div className="text-lg font-bold text-gray-900 tabular-nums">{loyerMensuel.toLocaleString('fr-FR')} €</div>
                </div>
              )}
              
              {property.charges_annuelles && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Charges annuelles</div>
                  <div className="text-lg font-bold text-gray-900">{property.charges_annuelles.toLocaleString()} €</div>
                </div>
              )}
              
              {chargesMensuelles != null && (
                <div className="p-3 bg-gray-50 rounded-lg" data-testid="public-legacy-charges">
                  <div className="text-xs text-gray-600 mb-1">Charges mensuelles</div>
                  <div className="text-lg font-bold text-gray-900 tabular-nums">{chargesMensuelles.toLocaleString('fr-FR')} €</div>
                </div>
              )}
              
              {property.loyer_annuel_charges_comprises && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Loyer CC/an</div>
                  <div className="text-lg font-bold text-gray-900">{property.loyer_annuel_charges_comprises.toLocaleString()} €</div>
                </div>
              )}
              
              {property.taxe_fonciere && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Taxe foncière</div>
                  <div className="text-lg font-bold text-gray-900">{property.taxe_fonciere.toLocaleString()} €</div>
                </div>
              )}
              
              {property.tva && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">TVA</div>
                  <div className="text-lg font-bold text-gray-900">{property.tva}</div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {property.description_technique || property.description_bien || 'Description non renseignée'}
              </p>
            </div>

            {/* Caractéristiques */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Caractéristiques</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                {isValidValue(property.superficie_totale) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Superficie totale</span>
                    <span className="font-semibold text-gray-900">{property.superficie_totale} m²</span>
                  </div>
                )}
                {isValidValue(property.surface_vente) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Surface de vente</span>
                    <span className="font-semibold text-gray-900">{property.surface_vente} m²</span>
                  </div>
                )}
                {isValidValue(property.surface_cuisine) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Surface cuisine</span>
                    <span className="font-semibold text-gray-900">{property.surface_cuisine} m²</span>
                  </div>
                )}
                {isValidValue(property.reserve) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Réserve</span>
                    <span className="font-semibold text-gray-900">{property.reserve} m²</span>
                  </div>
                )}
                {isValidValue(property.nombre_couverts_total) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Nombre de couverts</span>
                    <span className="font-semibold text-gray-900">{property.nombre_couverts_total}</span>
                  </div>
                )}
                {isValidValue(property.places_assises_interieur) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Places intérieur</span>
                    <span className="font-semibold text-gray-900">{property.places_assises_interieur}</span>
                  </div>
                )}
                {isValidValue(property.places_assises_terrasse) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Places terrasse</span>
                    <span className="font-semibold text-gray-900">{property.places_assises_terrasse}</span>
                  </div>
                )}
                {isValidValue(property.terrasse_m2) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Surface terrasse</span>
                    <span className="font-semibold text-gray-900">{property.terrasse_m2} m²</span>
                  </div>
                )}
                {isValidValue(property.type_cuisine) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Type de cuisine</span>
                    <span className="font-semibold text-gray-900">{property.type_cuisine}</span>
                  </div>
                )}
                {isValidValue(property.cuisine_equipee) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Équipement cuisine</span>
                    <span className="font-semibold text-gray-900">{property.cuisine_equipee}</span>
                  </div>
                )}
                {isValidValue(property.chambre_froide) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Chambre froide</span>
                    <span className="font-semibold text-gray-900">{property.chambre_froide}</span>
                  </div>
                )}
                {isValidValue(property.licence_restaurant) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Licence</span>
                    <span className="font-semibold text-gray-900">{property.licence_restaurant}</span>
                  </div>
                )}
                {isValidValue(property.parking_type) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Parking</span>
                    <span className="font-semibold text-gray-900">{property.parking_type}</span>
                  </div>
                )}
                {/* lot A — « Travaux à prévoir » n'est PAS diffusé : argument de négociation,
                    interne uniquement (retiré aussi de la projection publique côté API). */}
                {isValidValue(property.nom_enseigne) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Enseigne</span>
                    <span className="font-semibold text-gray-900">{property.nom_enseigne}</span>
                  </div>
                )}
                {isValidValue(property.brasseur) && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Brasseur</span>
                    <span className="font-semibold text-gray-900">{property.brasseur}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Contact 1/3 largeur */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Contactez-nous</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl text-center border border-blue-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                    <i className="bi bi-building text-white text-2xl"></i>
                  </div>
                  <p className="font-bold text-gray-900 text-lg mb-1">Votre Agence</p>
                  <p className="text-sm text-gray-600 mb-3">Experts en immobilier commercial</p>
                  
                  {/* Coordonnées agence */}
                  <div className="space-y-2 text-sm text-left mt-4 pt-4 border-t border-blue-200">
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="bi bi-telephone-fill text-blue-600"></i>
                      <a href="tel:+33123456789" className="hover:text-blue-600 font-medium">
                        01 23 45 67 89
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="bi bi-envelope-fill text-blue-600"></i>
                      <a href="mailto:contact@agence.fr" className="hover:text-blue-600 font-medium truncate">
                        contact@agence.fr
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="bi bi-globe text-blue-600"></i>
                      <a href="https://votre-agence.fr" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 font-medium truncate">
                        votre-agence.fr
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <i className="bi bi-instagram text-blue-600"></i>
                      <a href="https://instagram.com/votre_agence" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 font-medium">
                        @votre_agence
                      </a>
                    </div>
                  </div>
                </div>

                <button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2">
                  <i className="bi bi-telephone-fill"></i>
                  Appeler maintenant
                </button>

                <button className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                  <i className="bi bi-envelope-fill"></i>
                  Envoyer un email
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                  <i className="bi bi-shield-check text-green-600"></i>
                  Annonce vérifiée et sécurisée
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <p className="text-center text-gray-600 text-sm">
            © 2024 Immobilier Commercial - Tous droits réservés
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicPropertyView;
